import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Tag,
  Progress,
  Divider,
  message,
  List,
  Avatar,
  Table
} from 'antd';
import {
  UserOutlined,
  UserAddOutlined,
  ReloadOutlined,
  TrophyOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  LoginOutlined,
  StarOutlined,
  EyeInvisibleOutlined,
  BookOutlined,
  BankOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const StudentReport = () => {
const [dateRange, setDateRange] = useState([
  dayjs().startOf('year'),   // January 1st, 00:00
  dayjs().endOf('month')     // End of current month
]);

  
  const [summaryData, setSummaryData] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real API calls using axios
  const fetchSummaryData = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/report/student/summary`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        from: dateRange[0].format('YYYY-MM-DD'),
        to: dateRange[1].format('YYYY-MM-DD')
      }
    });
    return response.data;
  };

  const fetchDetailsData = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/report/student/new/details`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        from: dateRange[0].format('YYYY-MM-DD'),
        to: dateRange[1].format('YYYY-MM-DD')
      }
    });
    return response.data;
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const [summary, details] = await Promise.all([
        fetchSummaryData(),
        fetchDetailsData()
      ]);
      
      setSummaryData(summary);
      setDetailsData(details);
      
      message.success('Report data loaded successfully');
    } catch (err) {
      console.error('Error loading report data:', err);
      
      let errorMessage = 'Failed to load report data';
      if (err.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.message || 'Invalid request parameters';
        } else {
          errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        // Request made but no response received
        errorMessage = 'Network error. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('student-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Student Analytics Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .ant-progress-outer { 
              display: inline-block !important; 
              width: 200px !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Process education level distribution data
  const educationLevelData = summaryData?.education_level_distribution ? 
    summaryData.education_level_distribution
      .filter(item => item.level && item.level !== null && item.count > 0)
      .map((item, index) => ({
        name: item.level,
        value: parseInt(item.count) || 0,
        color: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#eb2f96', '#13c2c2'][index % 7]
      }))
      .sort((a, b) => b.value - a.value) // Sort by count descending
    : [];

  // Process school distribution data
  const schoolDistributionData = summaryData?.school_distribution ? 
    summaryData.school_distribution
      .filter(item => item.school && item.school !== null && item.count > 0)
      .map((item, index) => ({
        name: item.school,
        value: parseInt(item.count) || 0,
        color: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#eb2f96', '#13c2c2', '#fa8c16', '#a0d911', '#722ed1'][index % 10]
      }))
      .sort((a, b) => b.value - a.value) // Sort by count descending
      .slice(0, 10) // Show top 10 schools
    : [];

  // Prepare existing chart data
  const ageDistributionData = detailsData ? [
    { name: 'Under 10', value: parseInt(detailsData.age_under_10) || 0, color: '#1890ff' },
    { name: '10-12', value: parseInt(detailsData.age_10_12) || 0, color: '#52c41a' },
    { name: '13-15', value: parseInt(detailsData.age_13_15) || 0, color: '#faad14' },
    { name: '16-18', value: parseInt(detailsData.age_16_18) || 0, color: '#f5222d' },
    { name: 'Above 18', value: parseInt(detailsData.age_above_18) || 0, color: '#722ed1' }
  ].filter(item => item.value > 0) : [];

  const genderData = detailsData ? [
    { name: 'Male', value: parseInt(detailsData.male_count) || 0, color: '#1890ff' },
    { name: 'Female', value: parseInt(detailsData.female_count) || 0, color: '#eb2f96' },
    { name: 'Unspecified', value: parseInt(detailsData.unspecified_gender_count) || 0, color: '#8c8c8c' }
  ].filter(item => item.value > 0) : [];

  const studentTypeData = summaryData ? [
    { name: 'New Students', value: parseInt(summaryData.new_students) || 0, color: '#52c41a' },
    { name: 'Repeat Students', value: parseInt(summaryData.repeat_students) || 0, color: '#1890ff' }
  ].filter(item => item.value > 0) : [];

  // Login engagement data
  const loginEngagementData = detailsData ? [
    { name: 'Logged In', value: parseInt(detailsData.logged_in_count) || 0, color: '#52c41a' },
    { name: 'Never Logged In', value: parseInt(detailsData.never_logged_in) || 0, color: '#f5222d' }
  ].filter(item => item.value > 0) : [];

  // Table columns for school distribution
  const schoolTableColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 60,
      render: (text, record, index) => (
        <Avatar size="small" style={{ 
          backgroundColor: index < 3 ? '#ffd700' : '#1890ff',
          fontSize: '12px'
        }}>
          {index + 1}
        </Avatar>
      ),
    },
    {
      title: 'School Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => (
        <Space>
          <BankOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Students',
      dataIndex: 'value',
      key: 'value',
      width: 100,
      render: (count) => (
        <Tag color="blue">
          {count}
        </Tag>
      ),
    },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}`}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="student-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Student Analytics Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Student Analytics Report
          </Title>
          <Text type="secondary">
            Comprehensive overview of student enrollment, demographics, and engagement
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!summaryData && !detailsData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Enhanced Key Metrics */}
          {(summaryData || detailsData) && (
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Students"
                    value={detailsData?.total_student_count || summaryData?.total_students || 0}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="New Students"
                    value={detailsData?.new_student_count || summaryData?.new_students || 0}
                    prefix={<UserAddOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    In selected period
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Active Users"
                    value={detailsData?.logged_in_count || 0}
                    prefix={<LoginOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Ever logged in
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Avg Spending"
                    value={summaryData?.avg_spending_per_student || 0}
                    prefix="$"
                    precision={2}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* First Row - Student Type and Login Engagement */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card 
                title="Student Type Distribution"
                extra={<TrophyOutlined />}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={studentTypeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#1890ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card 
                title="Login Engagement (All Students)"
                extra={<LoginOutlined />}
              >
                {loginEngagementData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={loginEngagementData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {loginEngagementData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="large">
                        {loginEngagementData.map((entry) => (
                          <Space key={entry.name}>
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Based on {detailsData?.total_student_count || 0} total students
                      </Text>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No login engagement data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Second Row - NEW: Education Level and Age Distribution */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card 
                title="Education Level Distribution"
                extra={<BookOutlined />}
              >
                {educationLevelData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={educationLevelData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                        />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {educationLevelData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="small" wrap>
                        {educationLevelData.map((entry) => (
                          <Space key={entry.name} size="small">
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text style={{ fontSize: '12px' }}>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No education level data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Age Distribution">
                {ageDistributionData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={ageDistributionData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent, value }) => `${name} (${value})`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {ageDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="small" wrap>
                        {ageDistributionData.map((entry) => (
                          <Space key={entry.name} size="small">
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text style={{ fontSize: '12px' }}>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No age distribution data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Third Row - Gender Distribution and School Distribution */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card title="Gender Distribution">
                {genderData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={genderData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {genderData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="large">
                        {genderData.map((entry) => (
                          <Space key={entry.name}>
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No gender distribution data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card 
                  title="Top Schools by Student Count"
                  extra={<BankOutlined />}
                >
                  {schoolDistributionData.length > 0 ? (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {schoolDistributionData.map((school, index) => (
                          <Card
                            key={school.name}
                            size="small"
                            style={{ 
                              width: '100%',
                              backgroundColor: index < 3 ? '#f6ffed' : '#fafafa',
                              border: index < 3 ? '1px solid #b7eb8f' : '1px solid #d9d9d9'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Avatar 
                                  size="small" 
                                  style={{ 
                                    backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#ffa940' : index === 2 ? '#52c41a' : '#1890ff',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {index + 1}
                                </Avatar>
                                <div>
                                  <Text strong style={{ fontSize: '14px' }}>{school.name}</Text>
                                  {index < 3 && (
                                    <div>
                                      <Tag 
                                        color={index === 0 ? 'gold' : index === 1 ? 'orange' : 'green'} 
                                        size="small"
                                      >
                                        {index === 0 ? '🥇 Top School' : index === 1 ? '🥈 2nd Place' : '🥉 3rd Place'}
                                      </Tag>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                                  {school.value}
                                </Text>
                                <div>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    students
                                  </Text>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </Space>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                      <Text type="secondary">No school distribution data available</Text>
                    </div>
                  )}
                </Card>
            </Col>
          </Row>

          {/* Fourth Row - Top Active Students and Key Insights */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card 
                title="Top Active Students (All Time)"
                extra={<StarOutlined />}
              >
                {detailsData?.top_active_students && detailsData.top_active_students.length > 0 ? (
                  <>
                    <List
                      dataSource={detailsData.top_active_students}
                      renderItem={(student, index) => (
                        <List.Item key={student.userid}>
                          <Card
                            size="small"
                            style={{ width: '100%' }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <Text strong>#{index + 1} - {student.nickname}</Text>
                              </div>
                              <div>
                                <Text type="secondary">{student.login_count} logins</Text>
                              </div>
                            </div>
                          </Card>
                        </List.Item>
                      )}
                    />
                    <div style={{ textAlign: 'center', marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Login counts from all time activity
                      </Text>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text type="secondary">No active student data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Key Insights & Metrics">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {detailsData && (
                    <>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Text>Login Engagement Rate</Text>
                          <Text strong style={{ color: '#52c41a' }}>
                            {detailsData.total_student_count > 0 
                              ? ((detailsData.logged_in_count / detailsData.total_student_count) * 100).toFixed(1)
                              : 0}%
                          </Text>
                        </div>
                        <Progress 
                          percent={detailsData.total_student_count > 0 
                            ? (detailsData.logged_in_count / detailsData.total_student_count) * 100
                            : 0} 
                          strokeColor="#52c41a"
                          showInfo={false}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Based on all {detailsData.total_student_count || 0} students
                        </Text>
                      </div>
                      
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Text>Inactive Students</Text>
                          <Text strong style={{ color: '#f5222d' }}>
                            {detailsData.total_student_count > 0 
                              ? ((detailsData.never_logged_in / detailsData.total_student_count) * 100).toFixed(1)
                              : 0}%
                          </Text>
                        </div>
                        <Progress 
                          percent={detailsData.total_student_count > 0 
                            ? (detailsData.never_logged_in / detailsData.total_student_count) * 100
                            : 0} 
                          strokeColor="#f5222d"
                          showInfo={false}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Never logged in
                        </Text>
                      </div>

                      <Divider />
                      
                      <Row gutter={16}>
                        <Col span={12}>
                          <Statistic
                            title="Most Active Student"
                            value={detailsData.top_active_students && detailsData.top_active_students.length > 0
                              ? detailsData.top_active_students[0].login_count
                              : 0}
                            suffix="logins"
                            valueStyle={{ fontSize: '16px' }}
                          />
                          {detailsData.top_active_students && detailsData.top_active_students.length > 0 && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {detailsData.top_active_students[0].nickname}
                            </Text>
                          )}
                        </Col>
                        <Col span={12}>
                          <div>
                            <Text type="secondary">Most Popular Education Level</Text>
                            <br />
                            <Text strong style={{ fontSize: '16px' }}>
                              {educationLevelData.length > 0 
                                ? educationLevelData[0].name
                                : 'N/A'
                              }
                            </Text>
                            {educationLevelData.length > 0 && (
                              <>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {educationLevelData[0].value} students
                                </Text>
                              </>
                            )}
                          </div>
                        </Col>
                      </Row>

                      <Divider />

                      <Row gutter={16}>
                        <Col span={12}>
                          <div>
                            <Text type="secondary">Top School</Text>
                            <br />
                            <Text strong style={{ fontSize: '14px' }}>
                              {schoolDistributionData.length > 0 
                                ? schoolDistributionData[0].name
                                : 'N/A'
                              }
                            </Text>
                            {schoolDistributionData.length > 0 && (
                              <>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {schoolDistributionData[0].value} students
                                </Text>
                              </>
                            )}
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Text type="secondary">Dominant Age Group</Text>
                            <br />
                            <Text strong style={{ fontSize: '16px' }}>
                              {ageDistributionData.length > 0 
                                ? ageDistributionData.reduce((max, current) => 
                                    current.value > max.value ? current : max
                                  ).name
                                : 'N/A'
                              }
                            </Text>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Education Levels Summary */}
          {detailsData && detailsData.education_levels && Array.isArray(detailsData.education_levels) && (
            <Card title="Education Levels Represented" style={{ marginBottom: '24px' }}>
              <Space size={[0, 8]} wrap>
                {detailsData.education_levels
                  .filter(level => level && level.trim() !== '' && level !== null)
                  .map((level, index) => (
                    <Tag key={index} color="blue" style={{ margin: '4px' }}>
                      {level}
                    </Tag>
                  ))}
              </Space>
              {(!detailsData.education_levels.some(level => level && level.trim() !== '' && level !== null)) && (
                <Text type="secondary">No education level data available</Text>
              )}
            </Card>
          )}

          {/* NEW: School Distribution Summary Card */}
          {schoolDistributionData.length > 0 && (
            <Card title="School Distribution Summary" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Total Schools Represented"
                    value={schoolDistributionData.length}
                    prefix={<BankOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Largest School Group"
                    value={schoolDistributionData.length > 0 ? schoolDistributionData[0].value : 0}
                    suffix="students"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Average Students per School"
                    value={schoolDistributionData.length > 0 
                      ? (schoolDistributionData.reduce((sum, school) => sum + school.value, 0) / schoolDistributionData.length).toFixed(1)
                      : 0}
                    precision={1}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              </Row>
              
              <Divider />
              
              <div>
                <Text strong>School Distribution Overview:</Text>
                <div style={{ marginTop: '12px' }}>
                  <Space size={[8, 8]} wrap>
                    {schoolDistributionData.slice(0, 5).map((school, index) => (
                      <Tag 
                        key={school.name} 
                        color={index === 0 ? 'gold' : index === 1 ? 'orange' : index === 2 ? 'green' : 'blue'}
                        style={{ margin: '2px', padding: '4px 8px' }}
                      >
                        {school.name} ({school.value})
                      </Tag>
                    ))}
                    {schoolDistributionData.length > 5 && (
                      <Tag color="default">
                        +{schoolDistributionData.length - 5} more schools
                      </Tag>
                    )}
                  </Space>
                </div>
              </div>
            </Card>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default withRoleAccess(StudentReport, ['superadmin']);